import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	QueryList,
	ViewChildren
}                             from '@angular/core';
import ace                    from '@assets/vendor/ace-builds/src-min-noconflict/ace';
import { BaseComponent }      from '@core/bases/base-component/base.component';
import { IDomTreeChangeData } from '@core/interfaces/common.interfaces';
import {
	CodeEditorPanelConfigType,
	CodeEditorPanelIdentifierEnum,
	ICodeEditorConfig,
	ICodeEditorPanel,
	SplitViewOrientationEnum
}                             from '@shared/components/code-editor/code-editor.model';

@Component( {
	selector: 'app-code-editor-window[codeEditorConfig][codePrimary]',
	templateUrl: './code-editor-window.component.html',
	styleUrls: [ './code-editor-window.component.scss' ]
} )
export class CodeEditorWindowComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

	private _codeEditorConfig: ICodeEditorConfig;
	private _codePrimary: string;
	private _codeSecondary: string;

	// genuine ace elements
	readonly aceGutterClassName = 'ace_gutter';
	readonly aceScrollerClassName = 'ace_scroller';

	readonly codeEditorPanelIdentifierEnum = CodeEditorPanelIdentifierEnum;
	/*
	 * Mainly for use in `*ngFor` repeater in view.
	 * Defines the order in which the editor panels are rendered and prevents
	 * re-rendering (and thus loss of association between view and `Ace.Editor`
	 * instances) in case of major changes in `codeEditorPanelMap`.
	 */
	readonly codeEditorPanelIdentifiers: CodeEditorPanelIdentifierEnum[] = [
		CodeEditorPanelIdentifierEnum.PRIMARY,
		CodeEditorPanelIdentifierEnum.SECONDARY
	];
	readonly gutterExtensionIdSuffix = '-gutterExtension';
	readonly splitViewOrientationEnum = SplitViewOrientationEnum;

	codeEditorPanelMap: Map<CodeEditorPanelIdentifierEnum, ICodeEditorPanel>;

	@Input() set codePrimary( code: string ) {
		this._codePrimary = code;

		if ( !this.codeEditorPanelMap?.size ) { return; }
		this.setCodeOfEditorInstance( CodeEditorPanelIdentifierEnum.PRIMARY, this._codePrimary );
	}

	@Input() set codeSecondary( code: string ) {
		this._codeSecondary = code;

		if ( !this.codeEditorPanelMap?.size ) { return; }
		this.setCodeOfEditorInstance( CodeEditorPanelIdentifierEnum.SECONDARY, this._codeSecondary );
	}

	@Input() set codeEditorConfig( codeEditorConfig: ICodeEditorConfig ) {
		this._codeEditorConfig = codeEditorConfig;

		if ( !this.codeEditorPanelMap ) {
			const codeEditorPanelMap: Map<CodeEditorPanelIdentifierEnum, ICodeEditorPanel> = new Map<CodeEditorPanelIdentifierEnum, ICodeEditorPanel>();
			this.codeEditorPanelIdentifiers.forEach(
				( identifier: CodeEditorPanelIdentifierEnum ) =>
					codeEditorPanelMap.set(
						identifier,
						{ config: this._codeEditorConfig[ identifier ] }
					)
			);

			this.codeEditorPanelMap = codeEditorPanelMap;
		} else {
			this.codeEditorPanelMap.forEach( ( editorPanel: ICodeEditorPanel, key: CodeEditorPanelIdentifierEnum ) => {
					editorPanel.config = this._codeEditorConfig[ key ];

					// TODO check if here should go some update functionality for at least the secondary editor panel
				}
			);
		}
	}

	get codeEditorConfig(): ICodeEditorConfig {
		return this._codeEditorConfig;
	}

	@Output() closeEditorWindow: EventEmitter<null> = new EventEmitter();

	@ViewChildren( 'editor' ) private _editorPanels: QueryList<ElementRef<HTMLElement>>; // Ace instances
	@ViewChildren( 'gutterExtension' ) private _gutterExtensions: QueryList<ElementRef<HTMLElement>>;

	constructor() {
		super();
	}

	private setCodeOfEditorInstance( codeEditorPanelIdentifier: CodeEditorPanelIdentifierEnum, code: string ): void {
		const aceEditorInstance: ace.Ace.Editor = this.codeEditorPanelMap?.get( codeEditorPanelIdentifier ).aceEditorInstance;
		if ( !aceEditorInstance || !code ) { return; }
		// update aceEditorInstance panel content, stringifier also formats the code
		aceEditorInstance.session.setValue( JSON.stringify( JSON.parse( code ), null, 2 ) );

		// beautifier seems to work after some delay
		// const timeout = setTimeout( () => {
		// 	clearTimeout( timeout );
		// 	aceBeautify?.beautify( aceEditorInstance.session );
		// }, 100 );
	}

	private destroy(): void {
		this.codeEditorPanelIdentifiers.forEach( ( identifier: CodeEditorPanelIdentifierEnum ) =>
			this.codeEditorPanelMap.get( identifier ).aceEditorInstance.destroy()
		);
	}

	private updateCodeEditorPanelMap( viewIdentifier: CodeEditorPanelIdentifierEnum, aceEditor: ace.Ace.Editor ): void {
		this.codeEditorPanelMap.set(
			viewIdentifier,
			{
				...this.codeEditorPanelMap.get( viewIdentifier ),
				aceEditorInstance: aceEditor
			}
		);
	}

	private subscribeToEvents( viewIdentifier: CodeEditorPanelIdentifierEnum, aceEditor: ace.Ace.Editor ): void {

		// scroll event
		// --- Event handling for box-shadow on vertical scrolling ---
		const aceEditorScroller: HTMLElement = Array.from( aceEditor.container.childNodes ).find(
			( child: HTMLElement ) => Array.from( child.classList )
			// class name of scroller may have a trailing whitespace, hence this mapping
			.map( ( className: string ) => className.replace( /\s/g, '' ) )
			.includes( this.aceScrollerClassName )
		) as HTMLElement;

		const aceEditorGutter: HTMLElement = Array.from( aceEditor.container.childNodes ).find(
			( child: HTMLElement ) => Array.from( child.classList ).includes( this.aceGutterClassName )
		) as HTMLElement;

		aceEditor.session.on( 'changeScrollTop', ( scrollTop: number ) => {

			this.logger.debug( `CodeEditor.${ viewIdentifier }.scrollTop:`, scrollTop );

			if ( !aceEditorScroller || !aceEditorGutter ) {
				this.logger.error( `CodeEditorWindowComponent:init->aceEditorScroller or aceEditorGutter not available` );
				return;
			}

			if ( scrollTop > 0 ) {
				/*
				 * Because Ace's built-in functionality changes the whole class list
				 * on horizontal scrolling, the appearance/disappearance of the
				 * box-shadow at the top of the 'ace_scroller ' `div` is done via
				 * setting/removing an additional HTML attribute at this `div`
				 */
				aceEditorScroller.setAttribute( 'ace_scroll-top', '' );
				aceEditorGutter.classList.add( 'ace_scroll-top' );
			} else {
				aceEditorScroller.removeAttribute( 'ace_scroll-top' );
				aceEditorGutter.classList.remove( 'ace_scroll-top' );
			}
		} );


		// change event
		aceEditor.on( 'change', ( change: ace.Ace.Delta ) => {

			// this.logger.log( change );
			// this.logger.log( aceEditor.getValue() );
			// const file: File = new File(
			// 	change.lines,
			// 	'changedName.manifest',
			// 	{
			// 		type: 'text/cache-manifest'
			// 	}
			// );
			//
			// if ( change.action === 'remove' ) {
			// 	this.fileDropService.buildImportDataList(
			// 		[ file ],
			// 		this.fileDropService?.getFileDropInputConfigByGroupId( this.fileDropInputGroupId )
			// 	);
			// }
		} );
	}

	private setupCodeEditor( aceEditor: ace.Ace.Editor, viewConfig: CodeEditorPanelConfigType ): void {
		// set editor view theme
		aceEditor.setTheme( `ace/theme/${ this.codeEditorConfig.theme ?? 'fcp_dark' }` );
		// set read-only mode (optional)
		aceEditor.setReadOnly( viewConfig?.isReadonly );
		// remove print margin (i.e. vertical line that indicates maximum/recommended line length)
		aceEditor.setShowPrintMargin( false );
		// set IT language
		aceEditor.session.setMode( 'ace/mode/json' );
	}

	private configAce(): void {
		ace.config.set( 'fontSize', '14px' );
		ace.config.set(
			'basePath',
			'./assets/vendor/ace-builds/src-min-noconflict'
		);
	}


	private init(): void {
		this._editorPanels.forEach( ( aceEditorRef: ElementRef<HTMLElement>, index: number ) => {
			const aceEditor: ace.Ace.Editor = ace.edit( aceEditorRef.nativeElement );
			let viewIdentifier: CodeEditorPanelIdentifierEnum;
			let viewConfig: CodeEditorPanelConfigType;
			let code: string;

			if ( index === 0 ) {
				viewIdentifier = CodeEditorPanelIdentifierEnum.PRIMARY;
				viewConfig = this._codeEditorConfig[ CodeEditorPanelIdentifierEnum.PRIMARY ];
				code = this._codePrimary;
			} else {
				viewIdentifier = CodeEditorPanelIdentifierEnum.SECONDARY;
				viewConfig = this._codeEditorConfig[ CodeEditorPanelIdentifierEnum.SECONDARY ];
				code = this._codeSecondary;
			}

			this.setupCodeEditor( aceEditor, viewConfig );
			this.subscribeToEvents( viewIdentifier, aceEditor );
			this.updateCodeEditorPanelMap( viewIdentifier, aceEditor );

			// set initial editor view content
			this.setCodeOfEditorInstance( viewIdentifier, code );
		} );
	}

	ngOnInit(): void {
		this.configAce();
	}

	ngAfterViewInit(): void {
		this.init();
	}

	ngOnDestroy(): void {
		this.destroy();
	}

	adjustGutterExtensionWidth(
		domTreeChangeEvent: IDomTreeChangeData,
		identifier: CodeEditorPanelIdentifierEnum
	): void {

		this.logger.debug( 'CodeEditorWindowComponent: DOM mutation event was received!', domTreeChangeEvent, identifier );

		domTreeChangeEvent.mutationRecords.forEach( ( mutationRecord: MutationRecord ) => {

			const mutationTarget: HTMLElement = mutationRecord.target as HTMLElement;

			// Ace editor gutter has changed
			if (
				mutationRecord.type === 'attributes'
				&& mutationRecord.attributeName === 'style'
				&& Array.from( mutationTarget.classList ).includes( this.aceGutterClassName )
			) {
				this._gutterExtensions?.forEach( ( gutterExtension: ElementRef<HTMLElement> ) => {
					if ( gutterExtension.nativeElement.id === `${ identifier }${ this.gutterExtensionIdSuffix }` ) {
						// TODO width needs to be set by element, which has the greater width (relevant for splitView topBottom)
						gutterExtension.nativeElement.style.width = mutationTarget.style.width;
					}
				} );
			}
		} );
	}

	close(): void {
		this.closeEditorWindow.emit();
	}
}
